import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { updateHreflangTags } from '../../utils/hreflang';

const faqItems = [
  {
    question: "Why can pricing vary?",
    answer: "Because clinical conditions differ. Each patient's medical situation is unique, and individual factors such as overall health, kidney function, presence of complications, and specific treatment requirements can affect the final cost determined by the treating hospital."
  },
  {
    question: "Are payments made to you or to the hospital?",
    answer: "Medical payments are handled directly with the hospital unless otherwise disclosed. We provide coordination support, and payment arrangements are clearly explained during the consultation process."
  },
  {
    question: "What is included in the estimated cost range?",
    answer: "The estimated range typically includes pre-transplant evaluation, surgery, hospitalization, medical coordination, immunosuppressive medications during hospital stay, and initial post-operative care. Final pricing is determined by the treating hospital after clinical evaluation."
  },
  {
    question: "Are there any hidden fees?",
    answer: "No hidden coordination fees. We provide transparent cost guidance. All costs are explained clearly before proceeding. Travel, accommodation, and long-term medications are separate expenses that are disclosed upfront."
  },
  {
    question: "Do you guarantee transplant outcomes?",
    answer: "No. We do not provide performance guarantees. Transplant outcomes depend on individual medical factors determined by treating physicians. We provide coordination and information support only."
  },
  {
    question: "How do I get a detailed cost estimate?",
    answer: "Contact us for a free consultation. After reviewing your medical information, we will provide general cost guidance based on your specific situation. Final medical pricing is determined exclusively by the treating hospital after clinical evaluation."
  }
];

export default function PricingPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  useEffect(() => {
    document.title = 'Kidney Transplant Cost | Transparent Pricing Overview';
    
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const updateLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.href = href;
    };

    updateMetaTag('description', 'Transparent kidney transplant cost overview for international patients. Estimated program range clearly labeled. Final pricing determined by treating hospital. No hidden fees.');
    updateMetaTag('keywords', 'kidney transplant cost, international transplant pricing, transparent medical costs, estimated transplant expenses, no hidden fees, hospital pricing');
    updateMetaTag('last-modified', new Date().toISOString().split('T')[0]);
    updateLinkTag('canonical', 'https://kidneypatientsupport.com/pricing');

    // Hreflang tags
    updateHreflangTags({
      enPath: '/pricing',
      jaPath: '/pricing' // Japanese pricing page exists
    });

    updateMetaTag('og:url', 'https://kidneypatientsupport.com/pricing', true);
    updateMetaTag('og:title', 'Transparent Cost Overview | International Kidney Transplant Support', true);
    updateMetaTag('og:description', 'Transparent cost overview for international kidney transplant coordination. Estimated program range clearly labeled. Final pricing determined by treating hospital.', true);
    updateMetaTag('og:type', 'website', true);

    updateMetaTag('twitter:url', 'https://kidneypatientsupport.com/pricing');
    updateMetaTag('twitter:title', 'Transparent Cost Overview | International Kidney Transplant Support');
    updateMetaTag('twitter:description', 'Transparent cost overview for international kidney transplant coordination. Estimated program range clearly labeled. Final pricing determined by treating hospital.');
    updateMetaTag('twitter:card', 'summary_large_image');

    const scripts: HTMLScriptElement[] = [];

    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqItems.map(item => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": { "@type": "Answer", "text": item.answer }
      }))
    });
    document.head.appendChild(faqScript);
    scripts.push(faqScript);

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      scripts.forEach(script => {
        if (script.parentNode) document.head.removeChild(script);
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center cursor-pointer">
            <img src="https://static.readdy.ai/image/523aa923c70fc5f766f2f9fe4af6dd5e/f1b13f3c5cdde047774c7297e509c16c.png" alt="Kidney Patient Support" className="h-12 w-auto" />
          </Link>
          
          <div className="hidden lg:flex items-center space-x-8">
            <Link to="/" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Home</Link>
            <Link to="/ethics" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Ethics</Link>
            <Link to="/complete-guide-kidney-transplant-abroad" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Guide</Link>
            <Link to="/countries" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Countries</Link>
            <Link to="/pricing" className="text-sm font-medium text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Pricing</Link>
            <Link to="/process" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Process</Link>
            <Link to="/contact" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Contact</Link>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <i className="ri-close-line text-2xl"></i>
            ) : (
              <i className="ri-menu-line text-2xl"></i>
            )}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-[72px] bg-white z-40 overflow-y-auto">
            <div className="flex flex-col p-6 space-y-4">
              <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Home</Link>
              <Link to="/ethics" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Ethics</Link>
              <Link to="/complete-guide-kidney-transplant-abroad" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Guide</Link>
              <Link to="/countries" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Countries</Link>
              <Link to="/pricing" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Pricing</Link>
              <Link to="/process" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Process</Link>
              <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Contact</Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center bg-gradient-to-br from-teal-50 to-emerald-50 pt-32 pb-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-teal-200 rounded-full opacity-20 blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-emerald-200 rounded-full opacity-20 blur-3xl"></div>
        </div>
        <div className="max-w-5xl mx-auto px-6 text-center relative z-10">
          <div className="inline-block bg-teal-100 text-teal-700 px-6 py-3 rounded-full mb-8">
            <span className="text-sm font-bold tracking-wide">TRANSPARENT COST GUIDANCE</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-8 leading-tight">
            Kidney Transplant Cost: <span className="text-teal-600">Transparent Overview</span>
          </h1>
          <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto mb-4">
            Understanding <strong>international kidney transplant costs</strong> helps with financial planning. We provide clear estimated ranges while <Link to="/ethics" className="text-teal-600 hover:text-teal-700 font-medium">maintaining full transparency</Link> about what's included.
          </p>
          <p className="text-gray-600 text-base leading-relaxed max-w-3xl mx-auto mb-10">
            Patients considering <Link to="/pakistan" className="text-teal-600 hover:text-teal-700 font-medium">kidney transplant in Pakistan</Link> or other destinations can use this overview for financial planning. Learn how the <Link to="/process" className="text-teal-600 hover:text-teal-700 font-medium">transplant process</Link> works alongside cost considerations.
          </p>
          <div className="bg-white rounded-3xl p-10 shadow-2xl border-4 border-teal-600 mb-8 inline-block">
            <p className="text-gray-600 text-lg mb-4">Estimated program range:</p>
            <div className="text-6xl font-bold text-teal-600 mb-2">USD 120,000–150,000</div>
            <p className="text-gray-600 text-base mb-4"><em>(Estimated)</em></p>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-4 mt-6">
              <p className="text-gray-700 text-sm leading-relaxed text-left">
                <strong>Important:</strong> Final medical pricing is determined by the treating hospital after clinical evaluation. Individual medical factors may affect final cost.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center justify-center bg-teal-600 text-white px-10 py-5 rounded-full text-lg font-bold hover:bg-teal-700 transition-colors space-x-3 whitespace-nowrap shadow-xl cursor-pointer">
              <span>Request Information</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link to="/process" className="inline-flex items-center justify-center bg-white text-teal-600 border-2 border-teal-600 px-10 py-5 rounded-full text-lg font-bold hover:bg-teal-50 transition-colors space-x-3 whitespace-nowrap cursor-pointer">
              <span>How the Process Works</span>
              <i className="ri-file-list-3-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* What's Included / Not Included Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Understanding <span className="text-teal-600">Kidney Transplant Costs</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Our transparent pricing focuses on essential medical services, ensuring clarity about what's included in the <strong>kidney transplant cost estimate</strong>.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* What IS Included */}
            <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-3xl p-10 border-2 border-teal-200">
              <div className="w-16 h-16 bg-teal-600 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-checkbox-circle-fill text-white text-3xl"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Included in the cost:</h3>
              <ul className="space-y-4">
                <li className="flex items-start text-gray-700 text-base">
                  <i className="ri-check-line text-teal-600 mt-1 mr-3 flex-shrink-0 text-xl font-bold"></i>
                  <span><strong>Pre-transplant medical evaluation and review</strong></span>
                </li>
                <li className="flex items-start text-gray-700 text-base">
                  <i className="ri-check-line text-teal-600 mt-1 mr-3 flex-shrink-0 text-xl font-bold"></i>
                  <span><strong>Kidney transplant surgery</strong></span>
                </li>
                <li className="flex items-start text-gray-700 text-base">
                  <i className="ri-check-line text-teal-600 mt-1 mr-3 flex-shrink-0 text-xl font-bold"></i>
                  <span><strong>Hospitalization and inpatient care</strong></span>
                </li>
                <li className="flex items-start text-gray-700 text-base">
                  <i className="ri-check-line text-teal-600 mt-1 mr-3 flex-shrink-0 text-xl font-bold"></i>
                  <span><strong>Medical coordination</strong> with the transplant hospital</span>
                </li>
                <li className="flex items-start text-gray-700 text-base">
                  <i className="ri-check-line text-teal-600 mt-1 mr-3 flex-shrink-0 text-xl font-bold"></i>
                  <span><strong>Immunosuppressive medications</strong> during hospital stay</span>
                </li>
                <li className="flex items-start text-gray-700 text-base">
                  <i className="ri-check-line text-teal-600 mt-1 mr-3 flex-shrink-0 text-xl font-bold"></i>
                  <span><strong>Initial post-operative medical care</strong></span>
                </li>
                <li className="flex items-start text-gray-700 text-base">
                  <i className="ri-check-line text-teal-600 mt-1 mr-3 flex-shrink-0 text-xl font-bold"></i>
                  <span><strong>Official medical records</strong> after surgery</span>
                </li>
              </ul>
            </div>

            {/* What is NOT Included */}
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-3xl p-10 border-2 border-orange-200">
              <div className="w-16 h-16 bg-orange-600 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-close-circle-fill text-white text-3xl"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">What is not included?</h3>
              <p className="text-gray-700 text-base mb-6">To avoid misunderstandings, the following items are not included in the medical cost:</p>
              <ul className="space-y-4">
                <li className="flex items-start text-gray-700 text-base">
                  <i className="ri-close-line text-orange-600 mt-1 mr-3 flex-shrink-0 text-xl font-bold"></i>
                  <span><strong>International airfare</strong></span>
                </li>
                <li className="flex items-start text-gray-700 text-base">
                  <i className="ri-close-line text-orange-600 mt-1 mr-3 flex-shrink-0 text-xl font-bold"></i>
                  <span><strong>Accommodation</strong> outside the hospital</span>
                </li>
                <li className="flex items-start text-gray-700 text-base">
                  <i className="ri-close-line text-orange-600 mt-1 mr-3 flex-shrink-0 text-xl font-bold"></i>
                  <span><strong>Visa and travel-related fees</strong></span>
                </li>
                <li className="flex items-start text-gray-700 text-base">
                  <i className="ri-close-line text-orange-600 mt-1 mr-3 flex-shrink-0 text-xl font-bold"></i>
                  <span><strong>Travel insurance</strong></span>
                </li>
                <li className="flex items-start text-gray-700 text-base">
                  <i className="ri-close-line text-orange-600 mt-1 mr-3 flex-shrink-0 text-xl font-bold"></i>
                  <span><strong>Expenses for accompanying family members</strong></span>
                </li>
                <li className="flex items-start text-gray-700 text-base">
                  <i className="ri-close-line text-orange-600 mt-1 mr-3 flex-shrink-0 text-xl font-bold"></i>
                  <span><strong>Long-term medications</strong> after discharge</span>
                </li>
              </ul>
              <div className="mt-6 bg-white rounded-2xl p-4 border border-orange-200">
                <p className="text-gray-700 text-sm">
                  These costs vary by country and personal situation and are explained individually during consultation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Other Providers Charge More */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              International Kidney Transplant Cost: <span className="text-orange-600">Why Ours Is Lower</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Many overseas kidney transplant providers charge <strong>USD 200,000 or more</strong>. The difference isn't medical quality—it's the elimination of multiple intermediary layers.
            </p>
          </div>

          <div className="bg-white rounded-3xl p-10 shadow-xl border border-gray-200 mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Multiple Intermediaries Involved</h3>
            <div className="flex flex-col space-y-4">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-line text-gray-700 text-xl"></i>
                </div>
                <div className="ml-4">
                  <p className="text-gray-900 font-semibold">Patient</p>
                </div>
              </div>
              <div className="ml-6 border-l-4 border-orange-600 pl-6 py-2">
                <i className="ri-arrow-down-line text-orange-600 text-2xl"></i>
              </div>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-building-line text-orange-600 text-xl"></i>
                </div>
                <div className="ml-4">
                  <p className="text-gray-900 font-semibold">Domestic broker <span className="text-orange-600 text-sm">(+Commission)</span></p>
                </div>
              </div>
              <div className="ml-6 border-l-4 border-orange-600 pl-6 py-2">
                <i className="ri-arrow-down-line text-orange-600 text-2xl"></i>
              </div>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-building-line text-orange-600 text-xl"></i>
                </div>
                <div className="ml-4">
                  <p className="text-gray-900 font-semibold">Overseas agent <span className="text-orange-600 text-sm">(+Commission)</span></p>
                </div>
              </div>
              <div className="ml-6 border-l-4 border-orange-600 pl-6 py-2">
                <i className="ri-arrow-down-line text-orange-600 text-2xl"></i>
              </div>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-building-line text-orange-600 text-xl"></i>
                </div>
                <div className="ml-4">
                  <p className="text-gray-900 font-semibold">Local coordinator <span className="text-orange-600 text-sm">(+Commission)</span></p>
                </div>
              </div>
              <div className="ml-6 border-l-4 border-orange-600 pl-6 py-2">
                <i className="ri-arrow-down-line text-orange-600 text-2xl"></i>
              </div>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-hospital-line text-gray-700 text-xl"></i>
                </div>
                <div className="ml-4">
                  <p className="text-gray-900 font-semibold">Hospital</p>
                </div>
              </div>
            </div>
            <div className="mt-8 bg-orange-50 rounded-2xl p-6 border border-orange-200">
              <p className="text-gray-700 text-base leading-relaxed">
                <strong>Each layer adds commission</strong>, dramatically increasing total cost. By the time the patient pays, the price has often doubled.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-10 shadow-xl border border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Our Direct Coordination Approach:</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-teal-50 rounded-2xl p-6 border border-teal-200">
                <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-hospital-line text-white text-xl"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">Direct coordination</h4>
                <p className="text-gray-700 text-sm">with medical institutions</p>
              </div>
              <div className="bg-teal-50 rounded-2xl p-6 border border-teal-200">
                <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-link-unlink text-white text-xl"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">No unnecessary</h4>
                <p className="text-gray-700 text-sm">middlemen</p>
              </div>
              <div className="bg-teal-50 rounded-2xl p-6 border border-teal-200">
                <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-close-circle-line text-white text-xl"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">No hidden</h4>
                <p className="text-gray-700 text-sm">commissions</p>
              </div>
              <div className="bg-teal-50 rounded-2xl p-6 border border-teal-200">
                <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-file-list-3-line text-white text-xl"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">Transparent</h4>
                <p className="text-gray-700 text-sm">itemized explanations</p>
              </div>
            </div>
            <div className="mt-8 bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-6 text-center">
              <p className="text-white text-xl font-bold">
                <i className="ri-arrow-right-line mr-3"></i>
                By simplifying the structure, we reduce cost while maintaining medical standards and ethical compliance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cost Comparison Table */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Kidney Transplant Cost Comparison <span className="text-teal-600">(Example)</span>
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-3xl overflow-hidden shadow-xl border border-gray-200">
              <thead>
                <tr className="bg-gradient-to-r from-teal-600 to-teal-700">
                  <th className="px-8 py-6 text-left text-white font-bold text-lg">Provider Type</th>
                  <th className="px-8 py-6 text-left text-white font-bold text-lg">Typical Cost</th>
                  <th className="px-8 py-6 text-left text-white font-bold text-lg">Reason</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="px-8 py-6 font-semibold text-gray-900">Multi-layer agencies</td>
                  <td className="px-8 py-6 text-orange-600 font-bold">USD 200,000+</td>
                  <td className="px-8 py-6 text-gray-700">Multiple intermediaries</td>
                </tr>
                <tr className="bg-teal-50">
                  <td className="px-8 py-6 font-semibold text-gray-900">Hospital-direct coordination (us)</td>
                  <td className="px-8 py-6 text-teal-600 font-bold">USD 120,000–150,000</td>
                  <td className="px-8 py-6 text-gray-700">Direct structure</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-gray-600 text-sm">Figures shown are for medical costs only.</p>
          </div>
        </div>
      </section>

      {/* Payment Structure */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Transplant Cost Payment: <span className="text-teal-600">Staged Options Available</span>
            </h2>
          </div>
          
          <div className="bg-white rounded-3xl p-10 border border-gray-200 mb-8">
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              <strong>Yes.</strong> Most programs use staged payments to help manage the <strong>kidney transplant cost</strong>, such as:
            </p>
            <ul className="space-y-4">
              <li className="flex items-start text-gray-700 text-base">
                <i className="ri-checkbox-circle-fill text-teal-600 mt-1 mr-4 flex-shrink-0 text-xl"></i>
                <span><strong>Initial deposit</strong></span>
              </li>
              <li className="flex items-start text-gray-700 text-base">
                <i className="ri-checkbox-circle-fill text-teal-600 mt-1 mr-4 flex-shrink-0 text-xl"></i>
                <span><strong>Pre-surgery payment</strong></span>
              </li>
              <li className="flex items-start text-gray-700 text-base">
                <i className="ri-checkbox-circle-fill text-teal-600 mt-1 mr-4 flex-shrink-0 text-xl"></i>
                <span><strong>Final payment</strong></span>
              </li>
            </ul>
            <div className="mt-6 bg-teal-50 rounded-2xl p-6 border border-teal-200">
              <p className="text-gray-700 text-base">
                The exact schedule depends on the hospital and country and is explained in advance. For country-specific details, review our <Link to="/pakistan" className="text-teal-600 hover:text-teal-700 font-medium">Pakistan kidney transplant cost</Link> page.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-10 border border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Are there hidden or unexpected costs?</h3>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              <strong>No.</strong><br />
              Before proceeding, we provide a clear explanation of all standard costs.
            </p>
            <p className="text-gray-700 text-base leading-relaxed">
              In rare cases where unexpected medical complications occur, any additional costs are explained immediately and transparently. There are no surprise charges.
            </p>
          </div>
        </div>
      </section>

      {/* Country-Specific Cost Differences */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Country-Specific <span className="text-teal-600">Transplant Cost Considerations</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              While the total <strong>kidney transplant cost range</strong> is similar across <Link to="/countries" className="text-teal-600 hover:text-teal-700 font-medium">our partner countries</Link>, specific details may vary depending on the destination:
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Link to="/india" className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-3xl p-8 border border-orange-100 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-orange-600 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-hospital-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">India</h3>
              <p className="text-gray-700 leading-relaxed">
                Comprehensive evaluation and experienced transplant teams
              </p>
            </Link>

            <Link to="/cambodia" className="bg-gradient-to-br from-green-50 to-teal-50 rounded-3xl p-8 border border-green-100 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-green-600 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-time-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Cambodia</h3>
              <p className="text-gray-700 leading-relaxed">
                In some cases, shorter timelines depending on eligibility
              </p>
            </Link>

            <Link to="/pakistan" className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-8 border border-blue-100 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-wallet-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Pakistan</h3>
              <p className="text-gray-700 leading-relaxed">
                Considered when balancing cost and timing
              </p>
            </Link>
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-gray-600 text-base">Each option is explained clearly based on your medical condition and eligibility.</p>
          </div>
        </div>
      </section>

      {/* Important Disclaimer */}
      <section className="py-16 bg-yellow-50 border-y border-yellow-200">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-white rounded-3xl p-10 border-2 border-yellow-200">
            <div className="flex items-start">
              <div className="w-14 h-14 bg-yellow-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-information-line text-yellow-600 text-2xl"></i>
              </div>
              <div className="ml-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Kidney Transplant Cost Disclaimer</h3>
                <ul className="space-y-3">
                  <li className="flex items-start text-gray-700 text-base">
                    <i className="ri-arrow-right-s-line text-yellow-600 mt-1 mr-3 flex-shrink-0"></i>
                    <span><strong>Final medical pricing is determined by the treating hospital after clinical evaluation.</strong></span>
                  </li>
                  <li className="flex items-start text-gray-700 text-base">
                    <i className="ri-arrow-right-s-line text-yellow-600 mt-1 mr-3 flex-shrink-0"></i>
                    <span><strong>This website provides cost guidance only.</strong></span>
                  </li>
                  <li className="flex items-start text-gray-700 text-base">
                    <i className="ri-arrow-right-s-line text-yellow-600 mt-1 mr-3 flex-shrink-0"></i>
                    <span><strong>No hidden coordination fees.</strong></span>
                  </li>
                  <li className="flex items-start text-gray-700 text-base">
                    <i className="ri-arrow-right-s-line text-yellow-600 mt-1 mr-3 flex-shrink-0"></i>
                    <span><strong>Individual medical factors may affect final cost.</strong></span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Kidney Transplant Cost: <span className="text-teal-600">Common Questions</span>
            </h2>
          </div>
          
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div key={index} className={`bg-gray-50 rounded-2xl border transition-all ${openFaqIndex === index ? 'border-teal-300 shadow-md' : 'border-gray-200'}`}>
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left cursor-pointer hover:bg-gray-100 transition-colors rounded-2xl"
                >
                  <h3 className="text-lg font-bold text-gray-900 pr-4">Q: {item.question}</h3>
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className={`${openFaqIndex === index ? 'ri-subtract-line' : 'ri-add-line'} text-teal-600 text-xl`}></i>
                  </div>
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 pb-6">
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-gray-700 text-base leading-relaxed">
                        <strong>A:</strong> {item.answer}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-white rounded-full opacity-10 blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-white rounded-full opacity-10 blur-3xl"></div>
        </div>
        <div className="max-w-5xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Questions About Transplant Costs?
          </h2>
          <p className="text-xl text-white/90 leading-relaxed mb-10 max-w-3xl mx-auto">
            Every patient's situation is different. Contact us for personalized <strong>kidney transplant cost guidance</strong> based on your specific circumstances. Final medical pricing is determined by the treating hospital.
          </p>
          <Link to="/contact" className="inline-flex items-center justify-center bg-white text-teal-600 px-12 py-6 rounded-full text-xl font-bold hover:bg-gray-100 transition-colors space-x-3 whitespace-nowrap shadow-2xl cursor-pointer">
            <span>Request Information</span>
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      {/* Low-Friction Micro CTA */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-gray-700 text-xl mb-6">
            Unsure if this is right for you?
          </p>
          <Link 
            to="/who-this-is-for"
            className="inline-flex items-center justify-center bg-white text-teal-600 border-2 border-teal-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-teal-50 transition-colors space-x-3 whitespace-nowrap cursor-pointer"
          >
            <span>Check Eligibility</span>
            <i className="ri-checkbox-circle-line"></i>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-teal-600 to-teal-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8">
            <p className="text-white/90 text-base leading-relaxed max-w-4xl mx-auto">
              <strong>This website provides coordination and informational support services only.</strong><br />
              Medical decisions and procedures are handled exclusively by licensed healthcare providers.
            </p>
          </div>

          {/* Core Pages */}
          <div className="border-t border-white/20 pt-8 mb-8">
            <h4 className="text-white font-bold text-sm tracking-wide mb-4 text-center">CORE PAGES</h4>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-2">
              <Link to="/pakistan" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Kidney Transplant in Pakistan</Link>
              <Link to="/pricing" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Transparent Cost Overview</Link>
              <Link to="/process" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">How the Process Works</Link>
              <Link to="/ethics" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Ethical Framework</Link>
              <Link to="/who-this-is-for" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Eligibility Guide</Link>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-white/80 text-sm mb-4 md:mb-0">
              &copy; 2025 Japan Ambis International. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <Link to="/privacy" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Privacy Policy</Link>
              <Link to="/ethics" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Ethics</Link>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
